// src/components/subcomponents/CustomerExplorerModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Panel } from "primereact/panel";
import { Divider } from "primereact/divider";
import { ScrollPanel } from "primereact/scrollpanel";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";

import { OrderService } from "../service/OrderService";
import OrderViewEdit from "./OrderViewEdit";

import { formatDateLong, formatDateWeekday, formatToUSD } from "../../utils";
import { add } from "date-fns";

const STORAGE_KEY = "customerExplorer_dateRange_v1";

// --- Image thumbnail helpers (best-effort, schema-agnostic) ---
const isProbablyImageUrl = (url) => {
  const u = String(url || "").trim();
  if (!u) return false;
  // quick allowlist of typical image extensions OR data/blob/http(s)
  return (
    /^https?:\/\//i.test(u) ||
    /^data:image\//i.test(u) ||
    /^blob:/i.test(u) ||
    /\.(png|jpg|jpeg|webp|gif|bmp|tiff|svg)(\?|#|$)/i.test(u)
  );
};

const pickUrlFromMaybeObj = (x) => {
  if (!x) return null;
  if (typeof x === "string") return isProbablyImageUrl(x) ? x : null;

  // common fields for file objects
  const candidates = [
    x.url,
    x.secure_url,
    x.path,
    x.location,
    x.downloadUrl,
    x.downloadURL,
    x.previewUrl,
    x.previewURL,
    x.thumbUrl,
    x.thumbURL,
    x.thumbnail,
    x.thumbnailUrl,
    x.thumbnailURL,
  ].filter(Boolean);

  for (const c of candidates) {
    if (isProbablyImageUrl(c)) return c;
  }

  // sometimes nested
  if (x.file && typeof x.file === "object") {
    const nested = pickUrlFromMaybeObj(x.file);
    if (nested) return nested;
  }

  return null;
};

const flattenToArray = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return [v];
};

const extractFirstOrderImageUrl = (order) => {
  if (!order) return null;

  // ✅ Add/adjust these as you discover your real schema in your data
  const buckets = [
    order.images,
    order.imageUrls,
    order.imageURLs,
    order.photos,
    order.photoUrls,
    order.photoURLs,
    order.attachments,
    order.files,
    order.uploads,
    order.referenceImages,
    order.referencePhotos,
    order.inspirationImages,
    order.inspirationPhotos,
    order.customer?.attachments,
    order.customer?.photos,
    order.customer?.images,

    // sometimes a single field
    order.image,
    order.photo,
    order.thumbnail,
    order.thumb,

    // occasionally buried in orderItems (example guesses)
    order?.orderItems?.[0]?.cakes?.[0]?.referenceImages,
    order?.orderItems?.[0]?.cakes?.[0]?.referencePhotos,
  ];

  for (const bucket of buckets) {
    const arr = flattenToArray(bucket);
    for (const item of arr) {
      const url = pickUrlFromMaybeObj(item);
      if (url) return url;
    }
  }

  return null;
};

// Optional: if Cloudinary, generate a fast small thumb.
// If not Cloudinary, just returns the original URL.
const toThumbUrl = (url, { w = 64, h = 64 } = {}) => {
  const u = String(url || "").trim();
  if (!u) return null;

  // Cloudinary pattern: .../upload/... -> inject transforms after upload/
  if (/res\.cloudinary\.com/i.test(u) && /\/upload\//i.test(u)) {
    return u.replace(
      "/upload/",
      `/upload/w_${w},h_${h},c_fill,q_auto,f_auto/`
    );
  }

  return u;
};


// --- Normalizers (match behavior, but safer) ---
const normStr = (s) =>
  String(s ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normEmail = (s) => normStr(s);
const normPhone = (s) => {
  const digits = String(s ?? "").replace(/[^\d]/g, "");
  // keep last 10 if present
  if (digits.length >= 10) return digits.slice(-10);
  return digits;
};

// --- Money calc: mirrors your OrderViewEdit logic (safe parsing) ---
const toNum = (v) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const calcOrderItemsTotal = (orderItems0) => {
  const oi = orderItems0 || {};
  let total = 0;

  // Cakes
  (oi.cakes || []).forEach((cake) => {
    (cake.tiers || []).forEach((tier) => {
      total += toNum(tier?.price);
      total += toNum(tier?.premiumFillingPrice);
      total += toNum(tier?.soakPrice);
      total += toNum(tier?.extrasPrice);
    });

    (cake.toppers || []).forEach((t) => (total += toNum(t?.price)));
    (cake.decoratingItems || []).forEach((d) => (total += toNum(d?.price)));
  });

  // Cookies / Cupcakes / CakePops / GeneralItems
  (oi.cookies || []).forEach((x) => (total += toNum(x?.quantity) * toNum(x?.price)));
  (oi.cupcakes || []).forEach((x) => (total += toNum(x?.quantity) * toNum(x?.price)));
  (oi.cakePops || []).forEach((x) => (total += toNum(x?.quantity) * toNum(x?.price)));
  (oi.generalItems || []).forEach((x) => (total += toNum(x?.quantity) * toNum(x?.price)));

  return total;
};

const calcOrderTotal = (order) => {
  const itemsTotal = calcOrderItemsTotal(order?.orderItems?.[0]);
  const deliveryFee = toNum(order?.deliveryFee);
  const fees = (order?.additionalFees || []).reduce((a, b) => a + toNum(b?.price), 0);
  const discounts = (order?.discounts || []).reduce((a, b) => a + toNum(b?.price), 0);
  return itemsTotal + deliveryFee + fees - discounts;
};

const isPaidish = (invoiceStatus) => {
  const s = String(invoiceStatus || "").toUpperCase();
  return (
    s === "PAID" ||
    s === "COMP" ||
    s === "ONLINE" ||
    s === "PARTIALLY_PAID" ||
    s === "PARTIALLY_REFUNDED"
  );
};

const safeDueDate = (order) => {
  const d = order?.dueDate ? new Date(order.dueDate) : null;
  return d && !isNaN(d.getTime()) ? d : null;
};

const buildCustomerKey = (order) => {
  // Your rule:
  // - Use NAME as the identity gate.
  // - If the name differs, they are NOT the same customer, even if email/phone match.
  const nameKey = normStr(order?.customer?.name);
  return nameKey || "(no-name)";
};

const summarizeBy = (arr, getKey, getValue) => {
  const map = new Map();
  for (const x of arr) {
    const k = getKey(x);
    if (!k) continue;
    const v = getValue ? getValue(x) : 1;
    map.set(k, (map.get(k) || 0) + v);
  }
  return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
};

const CustomerExplorerModal = ({ productProperties = {}, orderProperties = {}, employees = [] }) => {
  const toast = useRef(null);

  const [visible, setVisible] = useState(false);

  // Separate date range from Orders page so we never compromise existing filters
  const [dateRange, setDateRange] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const start = new Date(parsed.start);
        const end = new Date(parsed.end);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) return { start, end };
      }
    } catch {}
    // Default: pre Jun 7 2023
    return { start: new Date("2023-06-01T00:00:00"), end: add(new Date(), { days: 0 }) };
  });

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ start: dateRange.start?.toISOString?.(), end: dateRange.end?.toISOString?.() })
    );
  }, [dateRange]);

  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  const [customerQuery, setCustomerQuery] = useState("");

  // ✅ single-customer selection
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // ✅ order selection + preview modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderPreviewVisible, setOrderPreviewVisible] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      if (!dateRange?.start || !dateRange?.end) throw new Error("Missing date range.");
      if (isNaN(dateRange.start.getTime()) || isNaN(dateRange.end.getTime())) throw new Error("Invalid date range.");
      if (dateRange.start > dateRange.end) throw new Error("Start date cannot be after end date.");

      const data = await OrderService.getOrdersByDateRange(dateRange.start, dateRange.end);

      // keep consistent with your other screens (skip canceled invoices)
      const filtered = (data || []).filter((o) => String(o?.invoiceStatus || "").toUpperCase() !== "CANCELED");

      // newest first
      filtered.sort((a, b) => {
        const da = safeDueDate(a)?.getTime?.() ?? 0;
        const db = safeDueDate(b)?.getTime?.() ?? 0;
        return db - da;
      });

      setOrders(filtered);
    } catch (err) {
      console.error("CustomerExplorerModal fetchOrders error:", err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err?.message || "Failed to fetch orders for customer explorer.",
      });
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Fetch only when modal opens (so we don't slow down Orders page)
  useEffect(() => {
    if (!visible) return;
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const customers = useMemo(() => {
    // Group orders by customer key (NAME-based identity)
    const map = new Map();

    for (const o of orders) {
      const name = String(o?.customer?.name || "").trim();
      const nameKey = buildCustomerKey(o);

      if (!map.has(nameKey)) {
        map.set(nameKey, {
          key: nameKey,
          displayName: name || "(No Name)",
          emails: new Set(),
          phones: new Set(),
          orders: [],
        });
      }

      const c = map.get(nameKey);
      const em = normEmail(o?.customer?.email);
      const ph = normPhone(o?.customer?.phone);

      if (em) c.emails.add(em);
      if (ph) c.phones.add(ph);

      c.orders.push(o);
    }

    // Convert to array + compute summary fields for table sorting/search
    const out = Array.from(map.values()).map((c) => {
      const ordersSorted = [...c.orders].sort((a, b) => {
        const da = safeDueDate(a)?.getTime?.() ?? 0;
        const db = safeDueDate(b)?.getTime?.() ?? 0;
        return db - da;
      });

      const totals = ordersSorted.reduce(
        (acc, o) => {
          const t = calcOrderTotal(o);
          acc.total += t;
          if (isPaidish(o?.invoiceStatus)) acc.paid += t;
          acc.count += 1;
          return acc;
        },
        { total: 0, paid: 0, count: 0 }
      );

      const lastOrder = ordersSorted[0];
      const lastDate = safeDueDate(lastOrder);

      return {
        ...c,
        orders: ordersSorted,
        emailList: Array.from(c.emails),
        phoneList: Array.from(c.phones),
        orderCount: totals.count,
        totalUsd: totals.total,
        paidUsd: totals.paid,
        lastOrderDate: lastDate ? lastDate.toISOString() : "",
      };
    });

    // Stable sort: most recent activity first
    out.sort((a, b) => {
      const da = a.lastOrderDate ? new Date(a.lastOrderDate).getTime() : 0;
      const db = b.lastOrderDate ? new Date(b.lastOrderDate).getTime() : 0;
      return db - da;
    });

    return out;
  }, [orders]);

  const filteredCustomers = useMemo(() => {
    const q = normStr(customerQuery);
    if (!q) return customers;

    return customers.filter((c) => {
      const hay = [
        c.displayName,
        ...(c.emailList || []),
        ...(c.phoneList || []),
        String(c.orderCount || ""),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [customers, customerQuery]);

  const customerRowClassName = useCallback(
    (rowData) => {
      return selectedCustomer?.key && selectedCustomer.key === rowData.key ? "p-highlight" : "";
    },
    [selectedCustomer]
  );

  const openOrderPreview = useCallback((order) => {
    if (!order) return;
    setSelectedOrder(order);
    setOrderPreviewVisible(true);
  }, []);

  const orderRowTemplate = useCallback((order) => {
  const due = safeDueDate(order);
  const inv = String(order?.invoiceStatus || "");
  const invUpper = inv.toUpperCase();

  let severity = "info";
  if (invUpper === "UNPAID") severity = "danger";
  if (invUpper === "PAID" || invUpper === "COMP" || invUpper === "ONLINE") severity = "success";
  if (invUpper === "PARTIALLY_PAID" || invUpper === "PARTIALLY_REFUNDED") severity = "warning";

  const img0 = extractFirstOrderImageUrl(order);
  const thumb = img0 ? toThumbUrl(img0, { w: 72, h: 72 }) : null;

  return (
    <div style={{ display: "flex", gap: "10px", alignItems: "stretch" }}>
      {/* Thumbnail */}
      <div
        style={{
          width: 54,
          height: 54,
          borderRadius: 10,
          overflow: "hidden",
          border: "1px solid #e5e5e5",
          background: "#fafafa",
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        title={thumb ? "Order has images" : "No images"}
      >
        {thumb ? (
          <img
            src={thumb}
            alt="Order thumbnail"
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => {
              // if thumb URL fails, hide the image and fall back to icon
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <i className="pi pi-image" style={{ fontSize: "1.1rem", opacity: 0.45 }} />
        )}
      </div>

      {/* Existing row content */}
      <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "10px" }}>
          <div style={{ fontWeight: 700 }}>
            {(order?.orderID ? `${order.orderID}: ` : "") + (order?.name || "(No Order Name)")}
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {order?.type ? <Tag value={order.type} /> : null}
            {inv ? <Tag severity={severity} value={inv} /> : null}
            {/* little indicator chip so it’s visible even if image fails to load */}
            {img0 ? <Tag value="IMG" severity="info" /> : null}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", opacity: 0.9 }}>
          <div>
            {due ? (
              <>
                {formatDateWeekday(due)} — {formatDateLong(due)}
              </>
            ) : (
              "No due date"
            )}
          </div>
          <div style={{ fontWeight: 700 }}>{formatToUSD(calcOrderTotal(order))}</div>
        </div>
      </div>
    </div>
  );
}, []);


  const buildNotesTimeline = useCallback((cust) => {
    const notes = [];

    for (const o of cust?.orders || []) {
      const due = safeDueDate(o);
      const dueLabel = due ? `${formatDateLong(due)} (${formatDateWeekday(due)})` : "No Date";
      const orderId = o?.orderID || o?._id || "(No ID)";

      const internalCustomerNotes = String(o?.customer?.internal_notes || "").trim();

      if (internalCustomerNotes) {
        notes.push({
          type: "Customer Internal Notes",
          orderId,
          dueLabel,
          text: internalCustomerNotes,
          sortTs: due?.getTime?.() ?? 0,
        });
      }
    }

    notes.sort((a, b) => (b.sortTs || 0) - (a.sortTs || 0));
    return notes;
  }, []);

  const renderCustomerSummary = useCallback(
    (cust) => {
      const custOrders = cust?.orders || [];
      const total = cust?.totalUsd || 0;
      const paid = cust?.paidUsd || 0;
      const count = cust?.orderCount || 0;
      const avg = count ? total / count : 0;

      const byInvoice = summarizeBy(
        custOrders,
        (o) => (o?.invoiceStatus ? String(o.invoiceStatus) : "(None)"),
        (o) => calcOrderTotal(o)
      );
      const byType = summarizeBy(
        custOrders,
        (o) => (o?.type ? String(o.type) : "(None)"),
        (o) => calcOrderTotal(o)
      );

      const notes = buildNotesTimeline(cust);

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Panel
            header={
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <div style={{ fontWeight: 800, fontSize: "1.1rem" }}>{cust.displayName}</div>
                <div style={{ opacity: 0.8, display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {(cust.emailList || []).slice(0, 3).map((e) => (
                    <Tag key={e} value={e} />
                  ))}
                  {(cust.phoneList || []).slice(0, 3).map((p) => (
                    <Tag key={p} value={p} />
                  ))}
                  {(cust.emailList || []).length > 3 ? <Tag value={`+${cust.emailList.length - 3} emails`} /> : null}
                  {(cust.phoneList || []).length > 3 ? <Tag value={`+${cust.phoneList.length - 3} phones`} /> : null}
                </div>
              </div>
            }
          >
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <div style={cardStyle}>
                <div style={cardLabelStyle}>Orders</div>
                <div style={cardValueStyle}>{count}</div>
              </div>
              <div style={cardStyle}>
                <div style={cardLabelStyle}>Total USD</div>
                <div style={cardValueStyle}>{formatToUSD(total)}</div>
              </div>
              <div style={cardStyle}>
                <div style={cardLabelStyle}>Paid-ish USD</div>
                <div style={cardValueStyle}>{formatToUSD(paid)}</div>
              </div>
              <div style={cardStyle}>
                <div style={cardLabelStyle}>Avg Order</div>
                <div style={cardValueStyle}>{formatToUSD(avg)}</div>
              </div>
            </div>

            <Divider />

            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <div style={{ minWidth: "260px" }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>Breakdown: Invoice Status (USD)</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {byInvoice.slice(0, 8).map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                      <span style={{ opacity: 0.9 }}>{k}</span>
                      <span style={{ fontWeight: 800 }}>{formatToUSD(v)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ minWidth: "260px" }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>Breakdown: Fulfillment Type (USD)</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {byType.slice(0, 8).map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                      <span style={{ opacity: 0.9 }}>{k}</span>
                      <span style={{ fontWeight: 800 }}>{formatToUSD(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <Divider />

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px" }}>
              <div>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>Internal Notes (by Order)</div>
                {notes.length === 0 ? (
                  <div style={{ opacity: 0.7 }}>No internal notes found on orders for this customer.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {notes.slice(0, 50).map((n, idx) => (
                      <div
                        key={`${n.orderId}-${idx}`}
                        style={{
                          border: "1px solid #e5e5e5",
                          borderRadius: 10,
                          padding: "10px",
                          background: "#fbfbfb",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                          <div style={{ fontWeight: 800 }}>{n.type}</div>
                          <div style={{ opacity: 0.8 }}>
                            <span style={{ fontWeight: 800, marginRight: 8 }}>{n.orderId}</span>
                            <span>{n.dueLabel}</span>
                          </div>
                        </div>
                        <div style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{n.text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Panel>

          <Panel header="Orders for this Customer (click to preview)">
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <DataTable
                value={custOrders}
                paginator
                rows={8}
                dataKey="_id"
                emptyMessage="No orders for this customer in the selected date range."
                selectionMode="single"
                selection={selectedOrder}
                onSelectionChange={(e) => {
                  const o = e.value || null;
                  if (o) openOrderPreview(o);
                }}
                onRowDoubleClick={(e) => openOrderPreview(e.data)}
              >
                <Column header="Order" body={(row) => orderRowTemplate(row)} style={{ width: "100%" }} />
              </DataTable>
            </div>
          </Panel>
        </div>
      );
    },
    [buildNotesTimeline, openOrderPreview, orderRowTemplate, selectedOrder]
  );

  const clearSelections = useCallback(() => {
    setSelectedCustomer(null);
    setSelectedOrder(null);
    setOrderPreviewVisible(false);
  }, []);

  const header = (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", width: "100%" }}>
      <div style={{ fontWeight: 900, fontSize: "1.1rem" }}>Customer Explorer</div>
      <div style={{ marginLeft: "auto", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontWeight: 700, opacity: 0.8 }}>From</span>
        <Calendar value={dateRange.start} onChange={(e) => setDateRange((r) => ({ ...r, start: e.value }))} showIcon />
        <span style={{ fontWeight: 700, opacity: 0.8 }}>To</span>
        <Calendar value={dateRange.end} onChange={(e) => setDateRange((r) => ({ ...r, end: e.value }))} showIcon />
        <Button
          label="Refresh"
          icon={loading ? "pi pi-spin pi-refresh" : "pi pi-refresh"}
          onClick={fetchOrders}
          disabled={loading}
        />
        <Button label="Clear" icon="pi pi-times" severity="secondary" onClick={clearSelections} disabled={loading} />
      </div>
    </div>
  );

  const orderPreviewHeader = useMemo(() => {
    if (!selectedOrder) return <div style={{ fontWeight: 800 }}>Order Preview</div>;

    return (
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
        <div style={{ fontWeight: 900, fontSize: "1.05rem", marginRight: 6 }}>Order Preview</div>
        {selectedOrder?.orderID ? <Tag value={selectedOrder.orderID} /> : null}
        {selectedOrder?.invoiceStatus ? <Tag value={selectedOrder.invoiceStatus} /> : null}
        {selectedOrder?.type ? <Tag value={selectedOrder.type} /> : null}
        <Tag value={formatToUSD(calcOrderTotal(selectedOrder))} />
      </div>
    );
  }, [selectedOrder]);

  return (
    <>
      <Toast ref={toast} />

      <Button label="Customer Search" icon="pi pi-users" onClick={() => setVisible(true)} size="small"/>

      {/* MAIN CUSTOMER EXPLORER */}
      <Dialog
        header={header}
        visible={visible}
        onHide={() => {
          setVisible(false);
          clearSelections();
        }}
        modal
        maximizable
        style={{ width: "98vw", height: "95vh" }}
        contentStyle={{ height: "calc(95vh - 110px)", overflow: "hidden" }}
      >
        <div style={{ height: "100%", display: "flex", gap: "10px", overflow: "hidden" }}>
          {/* Left: Customer list */}
          <div
            style={{
              width: "34%",
              minWidth: 340,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              overflow: "hidden",
            }}
          >
            <Panel header="Customers">
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <InputText
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                  placeholder="Search customers by name / email / phone..."
                  style={{ width: "100%" }}
                />
                <div style={{ opacity: 0.8, display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <Tag value={`Customers: ${filteredCustomers.length}`} />
                  <Tag value={`Orders Loaded: ${orders.length}`} />
                  {selectedCustomer?.displayName ? <Tag value={`Selected: ${selectedCustomer.displayName}`} /> : null}
                </div>
              </div>
            </Panel>

            <div style={{ flex: 1, overflow: "hidden" }}>
              <Panel header="Select Customer (single)">
                {loading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "30px" }}>
                    <ProgressSpinner />
                  </div>
                ) : (
                  <DataTable
                    value={filteredCustomers}
                    paginator
                    rows={10}
                    scrollable
                    scrollHeight="420px"
                    dataKey="key"
                    selectionMode="single"
                    selection={selectedCustomer}
                    onSelectionChange={(e) => {
                      const cust = e.value || null;
                      setSelectedCustomer(cust);
                      setSelectedOrder(null);
                      setOrderPreviewVisible(false);
                    }}
                    rowClassName={customerRowClassName}
                    emptyMessage="No customers found."
                  >
                    <Column
                      header="Customer"
                      body={(c) => (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div style={{ fontWeight: 800 }}>{c.displayName}</div>
                          <div style={{ opacity: 0.85, display: "flex", gap: "6px", flexWrap: "wrap" }}>
                            {c.emailList?.[0] ? <Tag value={c.emailList[0]} /> : null}
                            {c.phoneList?.[0] ? <Tag value={c.phoneList[0]} /> : null}
                            <Tag value={`${c.orderCount} orders`} />
                          </div>
                        </div>
                      )}
                    />
                    <Column
                      header="Total"
                      body={(c) => <div style={{ fontWeight: 800 }}>{formatToUSD(c.totalUsd || 0)}</div>}
                      style={{ width: "140px" }}
                    />
                  </DataTable>
                )}
              </Panel>
            </div>
          </div>

          {/* Right: Selected customer summary */}
          <div style={{ width: "66%", display: "flex", flexDirection: "column", gap: "10px", overflow: "hidden" }}>
            <Panel header="Customer Summary / Notes">
              <div style={{ opacity: 0.85 }}>
                {selectedCustomer ? "Click an order to open the preview modal." : "Select a customer to view their history."}
              </div>
            </Panel>

            <div style={{ flex: 1, overflow: "hidden" }}>
              <ScrollPanel style={{ height: "100%" }}>
                {!selectedCustomer ? (
                  <div style={{ padding: 20, opacity: 0.8 }}>No customer selected.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingRight: "10px" }}>
                    {renderCustomerSummary(selectedCustomer)}
                  </div>
                )}
              </ScrollPanel>
            </div>
          </div>
        </div>
      </Dialog>

      {/* ✅ ORDER PREVIEW AS ITS OWN POP-UP MODAL */}
      <Dialog
        header={orderPreviewHeader}
        visible={orderPreviewVisible}
        onHide={() => setOrderPreviewVisible(false)}
        modal
        maximizable
        style={{ width: "98vw", height: "95vh" }}
        contentStyle={{ height: "calc(95vh - 110px)", overflow: "hidden" }}
      >
        {!selectedOrder ? (
          <div style={{ padding: 20, opacity: 0.8 }}>No order selected.</div>
        ) : (
          <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: "10px", overflow: "hidden" }}>
            <Panel header="Order Preview (Read Only)">
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {selectedOrder?.orderID ? <Tag value={selectedOrder.orderID} /> : null}
                {selectedOrder?.invoiceStatus ? <Tag value={selectedOrder.invoiceStatus} /> : null}
                {selectedOrder?.type ? <Tag value={selectedOrder.type} /> : null}
                <Tag value={formatToUSD(calcOrderTotal(selectedOrder))} />
              </div>
            </Panel>

            <div style={{ flex: 1, overflow: "hidden" }}>
              <ScrollPanel style={{ height: "100%" }}>
                <div style={{ paddingRight: "10px" }}>
                  <OrderViewEdit
                    order={selectedOrder}
                    selectedOrderInvoice={null}
                    productProperties={productProperties}
                    orderProperties={orderProperties}
                    editable={false}
                    employees={employees}
                    setEditable={() => {}}
                    refreshOrders={() => {}}
                    setSelectedOrder={() => {}}
                    linkInvoiceToOrder={() => {}}
                    PotentialInvoices={null}
                    isSaving={false}
                    setIsSaving={() => {}}
                    customers={[]}
                    filteredPhoneNumbers={[]}
                    filteredEmails={[]}
                  />
                </div>
              </ScrollPanel>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

const cardStyle = {
  border: "1px solid #e5e5e5",
  borderRadius: 12,
  padding: "10px 12px",
  background: "#fbfbfb",
  minWidth: 170,
};

const cardLabelStyle = {
  fontWeight: 800,
  opacity: 0.7,
  fontSize: "0.85rem",
};

const cardValueStyle = {
  fontWeight: 900,
  fontSize: "1.2rem",
  marginTop: 4,
};

export default CustomerExplorerModal;
